import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import Logo from './Logo';

/**
 * @typedef {Object} NavigationItem
 * @property {string} label - Navigation item label
 * @property {() => void} onClick - Click handler for navigation item
 */

/**
 * @typedef {Object} MobileDrawerProps
 * @property {boolean} open - Whether the drawer is open
 * @property {() => void} onClose - Function to close the drawer
 * @property {NavigationItem[]} navigationItems - Array of navigation items
 * @property {() => void} onLogoClick - Click handler for logo in drawer
 * @property {Object} [sx] - Additional Material-UI sx props
 */

/**
 * Mobile Drawer component
 *
 * A reusable mobile navigation drawer with consistent styling and
 * navigation functionality for the RSM Collective header.
 *
 * @param {MobileDrawerProps} props - Component props
 * @returns {JSX.Element} Mobile navigation drawer
 */
function MobileDrawer({
  open,
  onClose,
  navigationItems,
  onLogoClick,
  sx = {},
}) {
  return (
    <Drawer
      variant="temporary"
      open={open}
      onClose={onClose}
      ModalProps={{
        keepMounted: true, // Better open performance on mobile
      }}
      sx={{
        display: { xs: 'block', lg: 'none' },
        '& .MuiDrawer-paper': {
          boxSizing: 'border-box',
          width: '100%',
          backgroundColor: 'secondary.main',
          color: 'secondary.contrastText',
          borderTop: '4px solid',
          borderTopColor: 'primary.main',
        },
        ...sx,
      }}
    >
      <Box sx={{ textAlign: 'center', py: 3 }}>
        {/* Close button */}
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
          }}
        >
          <IconButton
            onClick={onClose}
            sx={{
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
            aria-label="Close menu"
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Logo in drawer */}
        <Box sx={{ mb: 3 }}>
          <Logo
            onClick={onLogoClick}
            sx={{
              height: '56px',
              transform: 'scale(1)',
              '&:hover': {
                transform: 'scale(1.05)',
              },
            }}
          />
        </Box>

        {/* Navigation items */}
        <List sx={{ px: 2 }}>
          {navigationItems.map((item) => (
            <ListItem key={item.label} disablePadding sx={{ mb: 1 }}>
              <ListItemButton
                sx={{
                  textAlign: 'center',
                  borderRadius: 2,
                  py: 2,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
                onClick={item.onClick}
              >
                <ListItemText
                  primary={item.label}
                  sx={{
                    '& .MuiListItemText-primary': {
                      fontSize: '1.1rem',
                      fontWeight: 500,
                      color: 'white',
                    },
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>
    </Drawer>
  );
}

export default MobileDrawer;
